import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { MapPin, Search, Filter, Grid, List, Eye, ShoppingCart, Package, ChevronLeft, ChevronRight, SlidersHorizontal, Monitor, Building2, Car } from 'lucide-react';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatPrice } from '@/lib/utils';
import { apiRequest, getSessionId } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

interface MediaOwnerInventoryProps {
  ownerId: string;
}

export function MediaOwnerInventory({ ownerId }: MediaOwnerInventoryProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Fetch actual inventory and filter by media owner
  const { data: allInventory, isLoading, error } = useQuery({
    queryKey: ['/api/billboards'],
  });

  // Add to cart mutation
  const addToCartMutation = useMutation({
    mutationFn: async (data: { billboardId: number; duration: string; sessionId: string }) => {
      return apiRequest("POST", "/api/cart", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/cart"] });
      toast({
        title: "Added to cart",
        description: "Billboard has been added to your cart successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to add billboard to cart. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleAddToCart = (billboardId: number) => {
    const sessionId = getSessionId();
    addToCartMutation.mutate({
      billboardId,
      duration: "1-month",
      sessionId,
    });
  };

  console.log('MediaOwnerInventory Debug:', {
    ownerId,
    allInventoryCount: allInventory?.length || 0,
    filteredCount: allInventory?.filter((item: any) => item.mediaOwner === ownerId).length || 0,
    sampleItems: allInventory?.slice(0, 3)?.map((item: any) => ({ id: item.id, title: item.title, mediaOwner: item.mediaOwner }))
  });

  const ownerInventory = allInventory?.filter((item: any) => 
    item.mediaOwner === ownerId
  ).map((item: any) => ({
    id: item.id,
    title: item.title,
    location: item.location,
    price: item.price,
    originalPrice: item.price,
    type: item.inventoryType === 'digital' ? 'Digital' : 
          item.inventoryType === 'static' ? 'Classic' : 
          item.inventoryType === 'Package' ? 'Package' : 'Other',
    size: item.sizeResolution || "Standard",
    impressions: item.viewCount || 0,
    duration: item.inventoryType === 'digital' ? "15 seconds" : "Static",
    image: item.imageUrl,
    availability: item.isAvailable ? "Available" : "Sold Out",
    features: ["Premium Location", "High Traffic", "Professional Display"],
    audienceType: item.audienceType || "general",
    screenSize: item.screenSize || "medium",
    venueType: item.venueType || "outdoor"
  })) || [];

  // State management
  const [inventory, setInventory] = useState<any[]>([]);
  const [filteredInventory, setFilteredInventory] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedAvailability, setSelectedAvailability] = useState('all');
  const [selectedPriceRange, setSelectedPriceRange] = useState('all');
  const [selectedAudienceType, setSelectedAudienceType] = useState('all');
  const [selectedSize, setSelectedSize] = useState('all');
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [sortBy, setSortBy] = useState('title');
  


  // Update inventory when data loads
  useEffect(() => {
    if (ownerInventory) {
      setInventory(ownerInventory);
      setFilteredInventory(ownerInventory);
    }
  }, [ownerInventory]);

  // Initialize filter from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const typeParam = urlParams.get('type');
    const locationParam = urlParams.get('location');
    
    if (typeParam) setSelectedType(typeParam);
    if (locationParam) setSelectedLocation(locationParam);
  }, []);

  // Enhanced filtering and search logic
  useEffect(() => {
    let filtered = [...inventory];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(item =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.location.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Type filter
    if (selectedType !== 'all') {
      filtered = filtered.filter(item => item.type === selectedType);
    }

    // Location filter
    if (selectedLocation !== 'all') {
      filtered = filtered.filter(item => item.location.includes(selectedLocation));
    }

    // Availability filter
    if (selectedAvailability !== 'all') {
      filtered = filtered.filter(item => item.availability === selectedAvailability);
    }

    // Price range filter
    if (selectedPriceRange !== 'all') {
      const [min, max] = selectedPriceRange.split('-').map(Number);
      filtered = filtered.filter(item => 
        max ? item.price >= min && item.price <= max : item.price >= min
      );
    }

    // Audience type filter
    if (selectedAudienceType !== 'all') {
      filtered = filtered.filter(item => item.audienceType === selectedAudienceType);
    }

    // Size filter
    if (selectedSize !== 'all') {
      filtered = filtered.filter(item => item.screenSize === selectedSize);
    }

    // Features filter
    if (selectedFeatures.length > 0) {
      filtered = filtered.filter(item =>
        selectedFeatures.every(feature =>
          item.features?.some((itemFeature: string) => itemFeature.includes(feature))
        )
      );
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'impressions':
          return b.impressions - a.impressions;
        case 'title':
        default:
          return a.title.localeCompare(b.title);
      }
    });

    setFilteredInventory(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [inventory, searchQuery, selectedType, selectedLocation, selectedAvailability, selectedPriceRange, selectedAudienceType, selectedSize, selectedFeatures, sortBy]);

  // Get unique values for filter options
  const locations = Array.from(new Set(inventory.map(item => item.location.split(',').pop()?.trim() || item.location)));
  const sizes = Array.from(new Set(inventory.map(item => item.screenSize)));
  const features = Array.from(new Set(inventory.flatMap(item => item.features || [])));
  const audienceTypes = Array.from(new Set(inventory.map(item => item.audienceType)));
  const inventoryTypes = Array.from(new Set(inventory.map(item => item.type)));

  // Pagination logic
  const totalPages = Math.ceil(filteredInventory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentInventory = filteredInventory.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFeatureToggle = (feature: string) => {
    setSelectedFeatures(prev =>
      prev.includes(feature)
        ? prev.filter(f => f !== feature)
        : [...prev, feature]
    );
  };



  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedType('all');
    setSelectedLocation('all');
    setSelectedAvailability('all');
    setSelectedPriceRange('all');
    setSelectedAudienceType('all');
    setSelectedSize('all');
    setSelectedFeatures([]);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enhanced Filter Sidebar and Main Content */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Advanced Filters Sidebar */}
        <div className="lg:w-80 space-y-6">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 flex items-center">
                <SlidersHorizontal className="h-5 w-5 mr-2" />
                Filters
              </h3>
              <Button variant="ghost" size="sm" onClick={clearAllFilters}>
                Clear All
              </Button>
            </div>

            <div className="space-y-6">
              {/* Search */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search inventory..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Inventory Type */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Inventory Type</label>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {inventoryTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Location */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Location</label>
                <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {locations.map(location => (
                      <SelectItem key={location} value={location}>{location}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Availability */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Availability</label>
                <Select value={selectedAvailability} onValueChange={setSelectedAvailability}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Availability" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Availability</SelectItem>
                    <SelectItem value="Available">Available</SelectItem>
                    <SelectItem value="Sold Out">Sold Out</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Price Range */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Price Range</label>
                <Select value={selectedPriceRange} onValueChange={setSelectedPriceRange}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Prices" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Prices</SelectItem>
                    <SelectItem value="0-100000">Under $100K</SelectItem>
                    <SelectItem value="100000-500000">$100K - $500K</SelectItem>
                    <SelectItem value="500000-1000000">$500K - $1M</SelectItem>
                    <SelectItem value="1000000-99999999">Over $1M</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Audience Type */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Audience Type</label>
                <Select value={selectedAudienceType} onValueChange={setSelectedAudienceType}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Audiences" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Audiences</SelectItem>
                    {audienceTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Screen Size */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Screen Size</label>
                <Select value={selectedSize} onValueChange={setSelectedSize}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Sizes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sizes</SelectItem>
                    {sizes.map(size => (
                      <SelectItem key={size} value={size}>{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Features */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Features</label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {features.map(feature => (
                    <div key={feature} className="flex items-center space-x-2">
                      <Checkbox
                        id={feature}
                        checked={selectedFeatures.includes(feature)}
                        onCheckedChange={() => handleFeatureToggle(feature)}
                      />
                      <label htmlFor={feature} className="text-sm text-gray-600">
                        {feature}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Content */}
        <div className="flex-1 space-y-6">
          {/* Top Controls */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {filteredInventory.length} inventory item{filteredInventory.length !== 1 ? 's' : ''} found
              </h2>
              <p className="text-gray-600">
                Showing {startIndex + 1}-{Math.min(endIndex, filteredInventory.length)} of {filteredInventory.length} results
              </p>
            </div>

            <div className="flex items-center gap-4">
              {/* Sort */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="title">Title (A-Z)</SelectItem>
                  <SelectItem value="price-low">Price (Low to High)</SelectItem>
                  <SelectItem value="price-high">Price (High to Low)</SelectItem>
                  <SelectItem value="impressions">Most Impressions</SelectItem>
                </SelectContent>
              </Select>

              {/* Items per page */}
              <Select value={itemsPerPage.toString()} onValueChange={(value) => setItemsPerPage(Number(value))}>
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">6</SelectItem>
                  <SelectItem value="12">12</SelectItem>
                  <SelectItem value="24">24</SelectItem>
                  <SelectItem value="48">48</SelectItem>
                </SelectContent>
              </Select>

              {/* View Mode Toggle */}
              <div className="flex bg-gray-100 p-1 rounded-lg">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className="px-3"
                >
                  <Grid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="px-3"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Inventory Grid/List */}
          {currentInventory.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <Package className="h-16 w-16 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No inventory found</h3>
              <p className="text-gray-600">
                {inventory.length === 0 
                  ? "This media owner doesn't have any inventory available yet."
                  : "Try adjusting your search or filter criteria."
                }
              </p>
            </div>
          ) : (
            <>
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {currentInventory.map((item) => (
                    <Card key={item.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                      <div className="relative">
                        <img 
                          src={item.image} 
                          alt={item.title}
                          className="w-full h-48 object-cover"
                        />
                        <Badge 
                          className={`absolute top-3 left-3 ${
                            item.availability === 'Available' 
                              ? 'bg-emerald-600' 
                              : 'bg-red-600'
                          }`}
                        >
                          {item.availability}
                        </Badge>
                        <Badge 
                          variant="secondary" 
                          className="absolute top-3 right-3"
                        >
                          {item.type}
                        </Badge>
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-sm text-gray-600 mb-2 flex items-center">
                          <MapPin className="h-3 w-3 mr-1" />
                          {item.location}
                        </p>
                        <div className="space-y-2 text-sm text-gray-600">
                          <div>Size: {item.size}</div>
                          <div>Impressions: {item.impressions.toLocaleString()}/month</div>
                          <div>Duration: {item.duration}</div>
                        </div>
                        <div className="mt-4 pt-4 border-t">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-lg font-bold text-emerald-600">
                              {formatPrice(item.price)}
                            </span>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleAddToCart(item.id)}
                              disabled={item.availability === 'Sold Out' || addToCartMutation.isPending}
                              className="flex-1"
                            >
                              {addToCartMutation.isPending ? (
                                'Adding...'
                              ) : (
                                <>
                                  <ShoppingCart className="h-4 w-4 mr-1" />
                                  Add to Cart
                                </>
                              )}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => window.location.href = `/billboard/${item.id}?from=store&ownerId=${ownerId}`}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {currentInventory.map((item) => (
                    <Card key={item.id} className="overflow-hidden">
                      <CardContent className="p-0">
                        <div className="flex flex-col md:flex-row">
                          <div className="relative md:w-64 h-48">
                            <img 
                              src={item.image} 
                              alt={item.title}
                              className="w-full h-full object-cover"
                            />
                            <Badge 
                              className={`absolute top-3 left-3 ${
                                item.availability === 'Available' 
                                  ? 'bg-emerald-600' 
                                  : 'bg-red-600'
                              }`}
                            >
                              {item.availability}
                            </Badge>
                          </div>
                          <div className="flex-1 p-6">
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                                <p className="text-gray-600 flex items-center mb-2">
                                  <MapPin className="h-4 w-4 mr-1" />
                                  {item.location}
                                </p>
                                <Badge variant="secondary">{item.type}</Badge>
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-bold text-emerald-600 mb-2">
                                  {formatPrice(item.price)}
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => handleAddToCart(item.id)}
                                    disabled={item.availability === 'Sold Out' || addToCartMutation.isPending}
                                  >
                                    {addToCartMutation.isPending ? 'Adding...' : 'Add to Cart'}
                                  </Button>
                                  <Button variant="outline" size="sm" onClick={() => window.location.href = `/billboard/${item.id}?from=store&ownerId=${ownerId}`}>
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-4 text-sm text-gray-600">
                              <div>
                                <span className="font-medium">Size:</span> {item.size}
                              </div>
                              <div>
                                <span className="font-medium">Impressions:</span> {item.impressions.toLocaleString()}/month
                              </div>
                              <div>
                                <span className="font-medium">Duration:</span> {item.duration}
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-6">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    
                    <div className="flex space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const page = i + 1;
                        return (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(page)}
                            className="w-8 h-8 p-0"
                          >
                            {page}
                          </Button>
                        );
                      })}
                      {totalPages > 5 && (
                        <>
                          <span className="px-2 py-1 text-gray-500">...</span>
                          <Button
                            variant={currentPage === totalPages ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(totalPages)}
                            className="w-8 h-8 p-0"
                          >
                            {totalPages}
                          </Button>
                        </>
                      )}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>

                  <div className="text-sm text-gray-500">
                    Page {currentPage} of {totalPages}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}